import { useState, type FormEventHandler, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal } from 'antd';
import { useAppSelector } from '@/store/hooks';
import type { RootState } from '@/store';
import './open-account-shell.scss';

/** The six steps of opening an Apex account, as the boards name them. */
export const OPEN_ACCOUNT_STEPS = [
  'Identity',
  'Goals',
  'Risk profile',
  'About you',
  'Disclosures',
  'Agreements',
] as const;

export type OpenAccountStep = 1 | 2 | 3 | 4 | 5 | 6;

type OpenAccountShellProps = {
  step: OpenAccountStep;
  title: ReactNode;
  intro?: ReactNode;
  /** Right of the eyebrow, e.g. "Question 1 of 4". */
  meta?: ReactNode;
  /** Under the eyebrow, e.g. the risk questions' progress bars. */
  progress?: ReactNode;
  footer: ReactNode;
  children?: ReactNode;
  /**
   * Defaults to asking, then leaving for Wealth. Nothing on these pages is
   * saved until its step is submitted, so the default never says "save".
   */
  onExit?: () => void;
  exitLabel?: string;
  /** Renders the card as a <form> with this submit handler. */
  onSubmit?: FormEventHandler<HTMLFormElement>;
  cardLabel?: string;
  /** Steps already covered by details on file (an existing investor's identity). */
  onFileSteps?: OpenAccountStep[];
  /** Renames steps for this run, e.g. step 5 is "Review" for an existing investor. */
  stepLabels?: Partial<Record<OpenAccountStep, string>>;
};

const selectFirmName = (state: RootState): string | undefined => {
  const name = state?.userinfo?.riaProfile?.profile?.name;
  return typeof name === 'string' && name.trim() ? name.trim() : undefined;
};

/**
 * The shell every account-opening step shares (boards Open*): a top bar with
 * Exit, the six steps on the left (a progress bar on phones), and
 * one card holding the step's form, with Back/Continue in its footer.
 */
const OpenAccountShell = ({
  step,
  title,
  intro,
  meta,
  progress,
  footer,
  children,
  onExit,
  exitLabel = 'Exit',
  onSubmit,
  cardLabel,
  onFileSteps = [],
  stepLabels = {},
}: OpenAccountShellProps) => {
  const navigate = useNavigate();
  const [confirmingExit, setConfirmingExit] = useState(false);
  const firmName = useAppSelector(selectFirmName);
  const steps = OPEN_ACCOUNT_STEPS.map(
    (name, index) => stepLabels[(index + 1) as OpenAccountStep] ?? name,
  );
  const isOnFile = (number: number) => onFileSteps.includes(number as OpenAccountStep);
  const stepName = steps[step - 1];
  const nextStep: string | undefined = steps
    .slice(step)
    .find((_, offset) => !isOnFile(step + offset + 1));
  const eyebrow = `Step ${step} of ${steps.length} · ${stepName}`;
  const Card = onSubmit ? 'form' : 'section';

  return (
    <div className="open-account">
      <header className="open-account-bar">
        <div className="open-account-brand">
          {firmName && (
            <>
              <span className="open-account-firm">{firmName}</span>
              <span className="open-account-slash" aria-hidden="true">
                /
              </span>
            </>
          )}
          <span className="open-account-flow">Open an account</span>
        </div>
        <button
          type="button"
          className="open-account-exit"
          onClick={onExit ?? (() => setConfirmingExit(true))}
        >
          {exitLabel}
        </button>
      </header>

      <div className="open-account-mobile-progress">
        <div className="open-account-mobile-progress-text">
          <strong>{eyebrow}</strong>
          {nextStep && <span>Next: {nextStep}</span>}
        </div>
        <div className="open-account-segments" aria-hidden="true">
          {steps.map((name, index) => (
            <span
              key={name}
              className={
                index + 1 === step
                  ? 'is-current'
                  : index + 1 < step || isOnFile(index + 1)
                    ? 'is-done'
                    : ''
              }
            />
          ))}
        </div>
      </div>

      <div className="open-account-body">
        <nav className="open-account-steps" aria-label="Steps">
          <ol>
            {steps.map((name, index) => {
              const number = index + 1;
              const onFile = number !== step && isOnFile(number);
              const done = !onFile && number < step;
              const state = onFile
                ? 'is-on-file'
                : done
                  ? 'is-done'
                  : number === step
                    ? 'is-current'
                    : '';
              return (
                <li
                  key={name}
                  className={state}
                  aria-current={number === step ? 'step' : undefined}
                >
                  <b aria-hidden="true">{onFile || done ? '✓' : number}</b>
                  {name}
                  {done && <span className="open-account-sr"> (done)</span>}
                  {onFile && <small className="open-account-on-file">On file</small>}
                </li>
              );
            })}
          </ol>
        </nav>

        <Card
          className="open-account-card"
          aria-label={cardLabel ?? stepName}
          onSubmit={onSubmit}
          noValidate={onSubmit ? true : undefined}
        >
          <div className="open-account-head">
            <div className="open-account-eyebrow-row">
              <div className="open-account-eyebrow">{eyebrow}</div>
              {meta && <span className="open-account-meta">{meta}</span>}
            </div>
            {progress}
            <h1 className="open-account-title">{title}</h1>
            {intro && <div className="open-account-intro">{intro}</div>}
          </div>
          {children}
          <div className="open-account-footer">{footer}</div>
        </Card>
      </div>

      <Modal
        open={confirmingExit}
        title="Leave account opening?"
        onCancel={() => setConfirmingExit(false)}
        footer={[
          <Button key="stay" onClick={() => setConfirmingExit(false)}>
            Keep going
          </Button>,
          <Button key="leave" type="primary" onClick={() => navigate('/home/wealth')}>
            Leave
          </Button>,
        ]}
        className="open-account-modal"
      >
        <p>Answers on this step haven’t been submitted yet and won’t be kept.</p>
      </Modal>
    </div>
  );
};

export default OpenAccountShell;
